import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Calculator } from "lucide-react";
import { DEEPSEEK_PRICING, getRate, type ModelKey } from "@/lib/deepseek-pricing";

const MODELS: { value: ModelKey; label: string }[] = [
  { value: "v4-flash", label: "V4-Flash" },
  { value: "v4-pro", label: "V4-Pro" },
];

const fmt = (n: number) =>
  n < 0.01
    ? `$${n.toFixed(4)}`
    : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PricingCalculator() {
  const [model, setModel] = useState<ModelKey>("v4-flash");
  const [inputTokens, setInputTokens] = useState(1_000_000);
  const [outputTokens, setOutputTokens] = useState(500_000);
  const [hitRatio, setHitRatio] = useState(50);
  const [peakHour, setPeakHour] = useState(false);
  const [requestsPerMonth, setRequestsPerMonth] = useState(1000);

  const result = useMemo(() => {
    const rate = getRate(model);
    const ratio = hitRatio / 100;
    const inputRate = ratio * rate.inputCacheHit + (1 - ratio) * rate.inputCacheMiss;
    let perCall =
      (inputTokens / DEEPSEEK_PRICING.perTokens) * inputRate +
      (outputTokens / DEEPSEEK_PRICING.perTokens) * rate.output;
    if (peakHour && DEEPSEEK_PRICING.peakSurcharge.applies && DEEPSEEK_PRICING.peakSurcharge.surchargePct) {
      perCall *= 1 + DEEPSEEK_PRICING.peakSurcharge.surchargePct / 100;
    }
    const monthly = perCall * requestsPerMonth;

    // Flash comparison
    const flashRate = getRate("v4-flash");
    const flashInput = ratio * flashRate.inputCacheHit + (1 - ratio) * flashRate.inputCacheMiss;
    const flashPerCall =
      (inputTokens / DEEPSEEK_PRICING.perTokens) * flashInput +
      (outputTokens / DEEPSEEK_PRICING.perTokens) * flashRate.output;

    return { perCall, monthly, flashPerCall };
  }, [model, inputTokens, outputTokens, hitRatio, peakHour, requestsPerMonth]);

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Calculator className="h-6 w-6 text-primary" />
          DeepSeek API Cost Calculator
        </CardTitle>
        <CardDescription>
          Estimate your monthly bill on V4-Flash or V4-Pro. Rates verified {DEEPSEEK_PRICING.lastVerified}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="calc-model">Model</Label>
            <select
              id="calc-model"
              value={model}
              onChange={(e) => setModel(e.target.value as ModelKey)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {MODELS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="calc-input-tokens">Input tokens / call</Label>
            <Input
              id="calc-input-tokens"
              type="number"
              min={0}
              value={inputTokens}
              onChange={(e) => setInputTokens(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="calc-output-tokens">Output tokens / call</Label>
            <Input
              id="calc-output-tokens"
              type="number"
              min={0}
              value={outputTokens}
              onChange={(e) => setOutputTokens(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="calc-cache-ratio">Cache-hit ratio on input</Label>
            <span className="text-sm font-mono text-primary">{hitRatio}%</span>
          </div>
          <Slider id="calc-cache-ratio" value={[hitRatio]} onValueChange={(v) => setHitRatio(v[0])} min={0} max={100} step={5} />
          <p className="text-xs text-muted-foreground">
            % of input tokens that repeat a recent prefix. Stable system prompts push this above 80%.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="calc-requests">Requests / month</Label>
            <Input
              id="calc-requests"
              type="number"
              min={0}
              value={requestsPerMonth}
              onChange={(e) => setRequestsPerMonth(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>

          <div className="flex items-end gap-3 pb-2">
            <Switch
              id="peakhour"
              checked={peakHour}
              onCheckedChange={setPeakHour}
              disabled={!DEEPSEEK_PRICING.peakSurcharge.applies}
            />
            <Label htmlFor="peakhour" className="text-sm">
              Peak hours ({DEEPSEEK_PRICING.peakSurcharge.windowUTC} UTC)
              <span className="block text-xs text-muted-foreground">
                {DEEPSEEK_PRICING.peakSurcharge.applies
                  ? "Peak rates are double the off-peak rates shown by default"
                  : "Announced but not yet active — disabled"}
              </span>
            </Label>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 pt-4 border-t">
          <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
            <div className="text-xs text-muted-foreground mb-1">Cost per call</div>
            <div className="text-2xl font-bold text-primary">{fmt(result.perCall)}</div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-xs text-muted-foreground mb-1">Estimated monthly</div>
            <div className="text-2xl font-bold">{fmt(result.monthly)}</div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-xs text-muted-foreground mb-1">Same workload on V4-Flash</div>
            <div className="text-2xl font-bold">{fmt(result.flashPerCall)}</div>
            <div className="text-xs text-muted-foreground">per call</div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Estimate only — confirm live rates on{" "}
          <a href={DEEPSEEK_PRICING.source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            DeepSeek's official pricing page
          </a>
          .
        </p>
      </CardContent>
    </Card>
  );
}
